import React, { useState, useEffect } from 'react';
import { Modal, Tabs, message } from 'antd';
import notificationServices from 'services/notificationServices';
import { checkEnableParseJSON } from '../helper/helper';
import { htmlToText } from './utils';
import { useContactInfo } from './useContactInfo';
import { SendNoteModalProps, EmailRecipient } from './types';
// import SmsComposeForm from './SmsComposeForm';
import EmailComposeForm from './EmailComposeForm';
import HistoryView from './HistoryView';
import DraftView from './DraftView';
import { useAppSelector } from 'app/hooks';
import { getUserId } from 'utils/sessionStorage';

const { TabPane } = Tabs;

const SendNoteModal: React.FC<SendNoteModalProps> = ({
  visible,
  onCancel,
  conversationId,
  userType,
}) => {
  const [activeTab, setActiveTab] = useState<'send-sms' | 'send-email'>('send-email');
  const [sendType, setSendType] = useState<'note' | 'email'>('email');
  // Sub-tabs for SMS tab: 'compose', 'history', 'draft'
  // const [smsSubTab, setSmsSubTab] = useState<'compose' | 'history' | 'draft'>('compose');
  // Sub-tabs for Email tab: 'compose', 'history', 'draft'
  const [emailSubTab, setEmailSubTab] = useState<'compose' | 'history' | 'draft'>('compose');
  const [data, setData] = React.useState<any>();
  const [sendHistoryData, setSendHistoryData] = React.useState<any>(null);
  // const [smsHistoryData, setSmsHistoryData] = React.useState<any>(null);
  const [emailHistoryData, setEmailHistoryData] = React.useState<any>(null);
  // const [smsDraftData, setSmsDraftData] = React.useState<any>(null);
  const [emailDraftData, setEmailDraftData] = React.useState<any>(null);
  const [historyLoading, setHistoryLoading] = React.useState<boolean>(false);
  const [draftLoading, setDraftLoading] = React.useState<boolean>(false);
  const userId = getUserId();
  const [loading, setLoading] = React.useState<boolean>(true);
  const basicInformationStore = useAppSelector((store) => store.basicInformation.myBasicInformation);
  // Use contact info hook
  const {
    emailList,
    // phoneList,
    emailContactInfo,
    // phoneContactInfo,
    contactLoading,
    // isSmsNotificationEnabled,
    isEmailNotificationEnabled,
  } = useContactInfo(userType, visible);

  // State for selected recipients (as tags/badges)
  const [selectedEmailRecipients, setSelectedEmailRecipients] = useState<EmailRecipient[]>([]);
  // const [selectedPhoneRecipients, setSelectedPhoneRecipients] = useState<PhoneRecipient[]>([]);

  // State for input values (for search/type)
  const [emailInputValue, setEmailInputValue] = useState<string>('');
  // const [phoneInputValue, setPhoneInputValue] = useState<string>('');

  // State for sending notes
  const [sendingNotes, setSendingNotes] = useState<boolean>(false);

  // State for Note content
  const [noteTitle, setNoteTitle] = useState<string>('');
  const [noteContent, setNoteContent] = useState<string>('');

  // State for Email content
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [emailBody, setEmailBody] = useState<string>('');

  const handleTabChange = (key: string) => {
    const newTab = key as 'send-sms' | 'send-email';
    setActiveTab(newTab);
    // Auto set sendType based on active tab
    // if (newTab === 'send-sms') {
    //   setSendType('note');
    // } else if (newTab === 'send-email') {
    if (newTab === 'send-email') {
      setSendType('email');
    }
  };

  // const handleSmsSubTabChange = (key: string) => {
  //   const newSubTab = key as 'compose' | 'history' | 'draft';
  //   setSmsSubTab(newSubTab);

  //   // Fetch data when switching to history or draft
  //   if (newSubTab === 'history') {
  //     getSendHistory('sms', false);
  //   } else if (newSubTab === 'draft') {
  //     getSendHistory('sms', true);
  //   }
  // };

  const handleEmailSubTabChange = (key: string) => {
    const newSubTab = key as 'compose' | 'history' | 'draft';
    setEmailSubTab(newSubTab);

    // Fetch data when switching to history or draft
    if (newSubTab === 'history') {
      getSendHistory('email', false);
    } else if (newSubTab === 'draft') {
      getSendHistory('email', true);
    }
  };

  // Clear all state when modal is closed
  const clearState = () => {
    setActiveTab('send-email');
    // setSendType('note');
    setSendType('email');
    // setSmsSubTab('compose');
    setEmailSubTab('compose');
    setNoteTitle('');
    setNoteContent('');
    setEmailSubject('');
    setEmailBody('');
    setSelectedEmailRecipients([]);
    // setSelectedPhoneRecipients([]);
    setEmailInputValue('');
    // setPhoneInputValue('');
    setSendingNotes(false);
    // setSmsHistoryData(null);
    setEmailHistoryData(null);
    // setSmsDraftData(null);
    setEmailDraftData(null);
    setSendHistoryData(null);
  };

  // Clear form data when switching between send types
  const clearFormData = () => {
    setNoteTitle('');
    setNoteContent('');
    setEmailSubject('');
    setEmailBody('');
    setSelectedEmailRecipients([]);
    // setSelectedPhoneRecipients([]);
    setEmailInputValue('');
    // setPhoneInputValue('');
  };

  const handleCancel = () => {
    clearState();
    onCancel();
  };

  const getData = () => {
    if (userId) {
      notificationServices
        .getNotification(userId)
        .then((res: any) => {
          setData(res);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching notification data:', error);
          // Set empty data object to allow modal to render
          setData({});
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  const getSendHistory = (sendType?: 'sms' | 'email', isDraft: boolean = false) => {
    if (userId) {
      if (isDraft) {
        setDraftLoading(true);
      } else {
        setHistoryLoading(true);
      }

      // If sendType is specified, fetch filtered history
      if (sendType) {
        const params = {
          send_type: sendType === 'sms' ? 'sms' : 'email',
          is_draft: isDraft,
        };

        notificationServices
          .getSendHistory(userId, params)
          .then((res: any) => {
            if (isDraft) {
              // if (sendType === 'sms') {
              //   setSmsDraftData(res);
              // } else {
                setEmailDraftData(res);
              // }
              setDraftLoading(false);
            } else {
              // if (sendType === 'sms') {
              //   setSmsHistoryData(res);
              // } else {
                setEmailHistoryData(res);
              // }
              setHistoryLoading(false);
            }
          })
          .catch((error) => {
            console.error(`Error fetching ${sendType} ${isDraft ? 'draft' : 'history'}:`, error);
            if (isDraft) {
              setDraftLoading(false);
            } else {
              setHistoryLoading(false);
            }
          });
      } else {
        // Fetch both SMS and Email history
        Promise.all([
          // notificationServices.getSendHistory(userId, { send_type: 'sms', is_draft: isDraft }),
          notificationServices.getSendHistory(userId, { send_type: 'email', is_draft: isDraft }),
        ])
          .then(([emailRes]) => {
            if (isDraft) {
              // setSmsDraftData(smsRes);
              setEmailDraftData(emailRes);
              setDraftLoading(false);
            } else {
              // setSmsHistoryData(smsRes);
              setEmailHistoryData(emailRes);
              setHistoryLoading(false);
            }
          })
          .catch((error) => {
            console.error(`Error fetching ${isDraft ? 'draft' : 'send history'}:`, error);
            if (isDraft) {
              setDraftLoading(false);
            } else {
              setHistoryLoading(false);
            }
          });
      }
    }
  };

  useEffect(() => {
    getData();
  }, [userId, visible, conversationId, userType]);

  // Handle delete draft
  const handleDeleteDraft = async (record: any) => {
    if (!record.id) {
      message.error('Invalid record ID');
      return;
    }

    try {
      await notificationServices.deleteSendHistory(record.id);
      message.success('Draft deleted successfully!');

      // Refresh draft data
      // if (activeTab === 'send-sms' && smsSubTab === 'draft') {
      //   getSendHistory('sms', true);
      // } else if (activeTab === 'send-email' && emailSubTab === 'draft') {
      if (activeTab === 'send-email' && emailSubTab === 'draft') {
        getSendHistory('email', true);
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      message.error('Failed to delete draft. Please try again.');
    }
  };

  // Handle edit draft
  const handleEditDraft = (record: any) => {
    // Determine if it's SMS or Email based on record data
    // Check for send_type field first, then check for specific fields
    const sendType = record.send_type || record.sendType;
    const hasSMSFields =
      record.message || record.phone_recipients?.length > 0 || sendType === 'sms';
    const hasEmailFields =
      record.subject || record.text || record.email_recipients?.length > 0 || sendType === 'email';

    // Clear all form data first
    clearFormData();

    // if (hasSMSFields && !hasEmailFields) {
    //   // Handle SMS draft
    //   const messageText = record.message || record.content || '';

    //   // Set SMS form data (không có title cho SMS)
    //   setNoteContent(messageText);

    //   // Handle recipients - array of objects with phone and name
    //   const phoneRecipients = Array.isArray(record.phone_recipients) ? record.phone_recipients : [];
    //   setSelectedPhoneRecipients(phoneRecipients);
    //   setSendType('note');
    //   // Switch to SMS tab and compose sub-tab
    //   setActiveTab('send-sms');
    //   setSmsSubTab('compose');
    // } else if (hasEmailFields) {
    if (hasEmailFields) {
      // Handle Email draft
      setEmailSubject(record.subject || '');
      setEmailBody(record.text || record.content || '');

      // Handle recipients - array of objects with email and name
      const emailRecipients = Array.isArray(record.email_recipients) ? record.email_recipients : [];
      setSelectedEmailRecipients(emailRecipients);
      setSendType('email');
      // Switch to Email tab and compose sub-tab
      setActiveTab('send-email');
      setEmailSubTab('compose');
    } 
    // else {
    //   // Fallback: try to determine by available fields
    //   if (record.message || record.content) {
    //     // Assume SMS if has message/content but no email fields
    //     const messageText = record.message || record.content || '';

    //     setNoteContent(messageText);
    //     // Handle recipients - array of objects with phone and name
    //     const phoneRecipients = Array.isArray(record.phone_recipients)
    //       ? record.phone_recipients
    //       : [];
    //     setSelectedPhoneRecipients(phoneRecipients);
    //     setSendType('note');
    //     // Switch to SMS tab and compose sub-tab
    //     setActiveTab('send-sms');
    //     setSmsSubTab('compose');
    //   }
    // }
  };

  // Handle sending notes or emails
  const handleSend = async () => {
    if (!userId) {
      message.error('User ID not found');
      return;
    }

    // if (sendType === 'note') {
    //   // Validate note content
    //   if (!noteContent.trim()) {
    //     message.error('Please enter note content');
    //     return;
    //   }

    //   // Determine if it's a draft (no recipients) or send (has recipients)
    //   // If no recipients, it's a save (draft) action, otherwise it's a send action
    //   const isDraft = selectedPhoneRecipients.length === 0;

    //   // Check for recipients with disabled SMS notifications (warning only, don't block)
    //   // Skip check for Anonymous recipients (manually typed)
    //   if (!isDraft) {
    //     const disabledRecipients = selectedPhoneRecipients.filter(
    //       (recipient) =>
    //         recipient.name !== 'Anonymous' && !isSmsNotificationEnabled(recipient.phone)
    //     );
    //     if (disabledRecipients.length > 0) {
    //       // Show warning but allow sending
    //       message.warning(
    //         `${disabledRecipients.length} recipient(s) have disabled receiving SMS notifications. Message will still be sent.`,
    //         5
    //       );
    //     }
    //   }

    //   setSendingNotes(true);

    //   try {
    //     // Gửi SMS với nội dung note (không có title)
    //     const smsMessage = noteContent;
    //     // Build selectedPhoneRecipients array with phone and name
    //     const selectedPhoneRecipientsData = selectedPhoneRecipients.map((recipient) => ({
    //       phone: recipient.phone,
    //       name: recipient.name,
    //     }));
    //     const requestBody = {
    //       user_id: parseInt(userId || '0'),
    //       recipients: selectedPhoneRecipientsData,
    //       message: smsMessage,
    //       is_draft: isDraft,
    //     };

    //     await notificationServices.sendSMS(requestBody);
    //     if (isDraft) {
    //       message.success('Note saved as draft successfully!');
    //       // Clear form data
    //       clearFormData();
    //       // Switch to draft sub-tab within SMS tab
    //       setSmsSubTab('draft');
    //       // Refresh draft data
    //       getSendHistory('sms', true);
    //     } else {
    //       message.success(
    //         `Note sent successfully to ${selectedPhoneRecipients.length} phone number(s)!`
    //       );
    //       // Clear form data
    //       clearFormData();
    //       // Switch to history sub-tab within SMS tab
    //       setSmsSubTab('history');
    //       // Refresh history data
    //       getSendHistory('sms', false);
    //     }
    //   } catch (error) {
    //     console.error('Error sending note:', error);
    //     message.error(
    //       isDraft
    //         ? 'Failed to save note. Please try again.'
    //         : 'Failed to send note. Please try again.'
    //     );
    //   } finally {
    //     setSendingNotes(false);
    //   }
    // } else {
    // Send Email
    if (!emailSubject.trim()) {
      message.error('Please enter an email subject');
      return;
    }
    if (!emailBody.trim()) {
      message.error('Please enter email body');
      return;
    }

    // Determine if it's a draft (no recipients) or send (has recipients)
    // If no recipients, it's a save (draft) action, otherwise it's a send action
    const isDraft = selectedEmailRecipients.length === 0;

    // Check for recipients with disabled email notifications (warning only, don't block)
    // Skip check for Anonymous recipients (manually typed)
    if (!isDraft) {
      const disabledRecipients = selectedEmailRecipients.filter(
        (recipient) =>
          recipient.name !== 'Anonymous' && !isEmailNotificationEnabled(recipient.email)
      );
      if (disabledRecipients.length > 0) {
        // Show warning but allow sending
        message.warning(
          `${disabledRecipients.length} recipient(s) have disabled receiving email notifications. Email will still be sent.`,
          5
        );
      }
    }

    setSendingNotes(true);

    try {
      // Gửi email
      // emailBody từ RichTextEditor đã là HTML, không cần convert
      const htmlContent = emailBody || '';
      const textContent = htmlToText(emailBody || '');
      
      // Build selectedEmailRecipients array with email and name
      const selectedEmailRecipientsData = selectedEmailRecipients.map((recipient) => ({
        email: recipient.email,
        name: recipient.name,
      }));
      const requestBody = {
        user_id: parseInt(userId || '0'),
        recipients: selectedEmailRecipientsData,
        subject: emailSubject,
        text: textContent,
        html: htmlContent,
        is_draft: isDraft,
        senderName: basicInformationStore?.first_name + ' ' + basicInformationStore?.last_name,
        senderEmail: basicInformationStore?.email,
      };

      await notificationServices.sendEmail(requestBody);
      if (isDraft) {
        message.success('Email saved as draft successfully!');
        // Clear form data
        clearFormData();
        // Switch to draft sub-tab within Email tab
        setEmailSubTab('draft');
        // Refresh draft data
        getSendHistory('email', true);
      } else {
        message.success(
          `Email sent successfully to ${selectedEmailRecipients.length} recipient(s)!`
        );
        // Clear form data
        clearFormData();
        // Switch to history sub-tab within Email tab
        setEmailSubTab('history');
        // Refresh history data
        getSendHistory('email', false);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      message.error(
        isDraft
          ? 'Failed to save email. Please try again.'
          : 'Failed to send email. Please try again.'
      );
    } finally {
      setSendingNotes(false);
    }
  };

  return (
    <>
      <style>
        {`
          .table-row-light {
            background-color: #ffffff !important;
          }
          .table-row-dark {
            background-color: #f8f9fa !important;
          }
          .ant-table-thead > tr > th {
            background-color: #f0f8ff !important;
            font-weight: 600 !important;
            color: #1890ff !important;
            border-bottom: 2px solid #e6f7ff !important;
            padding: 12px 8px !important;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: #e6f7ff !important;
          }
          .ant-table-tbody > tr > td {
            border-bottom: 1px solid #f0f0f0 !important;
            padding: 12px 8px !important;
            vertical-align: top !important;
          }
          .ant-table-container {
            border-radius: 8px !important;
            overflow: hidden !important;
          }
          .ant-table {
            margin-top: 0 !important;
          }
          .ant-table-thead {
            position: relative !important;
          }
          .ant-card-body {
            padding: 16px !important;
          }
          .ant-table-wrapper {
            margin-top: 0 !important;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .ant-tag-close-icon {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin-left: 4px !important;
            vertical-align: middle !important;
            line-height: 1 !important;
          }
          .ant-tag {
            display: inline-flex !important;
            align-items: center !important;
          }
        `}
      </style>
      <Modal
        title={
          <div
            style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#1890ff',
            }}
          >
            Emails {/* <span className='mx-2' style={{ fontWeight: 'bold', color: '#1890ff', }}>|</span> Messages{' '}
            <span className='mx-2' style={{ fontWeight: 'bold', color: '#1890ff' }}>|</span> Notes */}
          </div>
        }
        visible={visible}
        onCancel={handleCancel}
        footer={null}
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ padding: '24px' }}
      >
        <Tabs activeKey={activeTab} onChange={handleTabChange} size="large">
          {(loading || contactLoading) && (
            <div
              style={{
                padding: '60px 0',
                textAlign: 'center',
                fontSize: '16px',
                color: '#666',
                margin: '0 auto',
              }}
            >
              <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
              {loading && contactLoading
                ? 'Loading notification settings and contact information...'
                : loading
                ? 'Loading notification settings...'
                : 'Loading contact information...'}
            </div>
          )}
          {!loading && !contactLoading && (
            <>
              {/* <TabPane tab="📝 Send/Save SMS" key="send-sms">
                <div style={{ padding: '16px 0' }}>
                  <Tabs activeKey={smsSubTab} onChange={handleSmsSubTabChange} type="card">
                    <TabPane tab="✏️ Compose" key="compose">
                      <SmsComposeForm
                        noteContent={noteContent}
                        onNoteContentChange={setNoteContent}
                        phoneList={phoneList}
                        phoneContactInfo={phoneContactInfo}
                        selectedPhoneRecipients={selectedPhoneRecipients}
                        phoneInputValue={phoneInputValue}
                        onPhoneInputChange={setPhoneInputValue}
                        onPhoneRecipientsChange={setSelectedPhoneRecipients}
                        // isSmsNotificationEnabled={() => true}
                        onSend={handleSend}
                        sendingNotes={sendingNotes}
                        contactLoading={contactLoading}
                        // isSendSMS={
                        //   checkEnableParseJSON(data?.email_notification_array, []).find((item: any) => item.noti_type === 'sms' && !item.type && item.is_enabled === true) ? true : false
                        // }
                        isSendSMS={true}
                      />
                    </TabPane>
                    <TabPane tab="📋 History" key="history">
                      <div style={{ padding: '16px 0' }}>
                        <HistoryView
                          type="sms"
                          data={smsHistoryData}
                          loading={historyLoading}
                        />
                      </div>
                    </TabPane>
                    <TabPane tab="📝 Drafts" key="draft">
                      <div style={{ padding: '16px 0' }}>
                        <DraftView
                          type="sms"
                          data={smsDraftData}
                          loading={draftLoading}
                          onEditDraft={handleEditDraft}
                          onDeleteDraft={handleDeleteDraft}
                        />
                      </div>
                    </TabPane>
                  </Tabs>
                </div>
              </TabPane> */}
              <TabPane tab="📧 Send/Save Notes" key="send-email">
                <div style={{ padding: '16px 0' }}>
                  <Tabs activeKey={emailSubTab} onChange={handleEmailSubTabChange} type="card">
                    <TabPane tab="✏️ Compose" key="compose">
                      <EmailComposeForm
                        emailSubject={emailSubject}
                        onEmailSubjectChange={setEmailSubject}
                        emailBody={emailBody}
                        onEmailBodyChange={setEmailBody}
                        emailList={emailList}
                        emailContactInfo={emailContactInfo}
                        selectedEmailRecipients={selectedEmailRecipients}
                        emailInputValue={emailInputValue}
                        onEmailInputChange={setEmailInputValue}
                        onEmailRecipientsChange={setSelectedEmailRecipients}
                        onSend={handleSend}
                        sendingNotes={sendingNotes}
                        contactLoading={contactLoading}
                        // isSendEmail={
                        //   checkEnableParseJSON(data?.email_notification_array, []).find((item: any) => item.noti_type === 'email' && !item.type && item.is_enabled === true) ? true : false
                        // }
                        isSendEmail={true}
                      />
                    </TabPane>
                    <TabPane tab="📋 History" key="history">
                      <div style={{ padding: '16px 0' }}>
                        <HistoryView
                          type="email"
                          data={emailHistoryData}
                          loading={historyLoading}
                        />
                      </div>
                    </TabPane>
                    <TabPane tab="📝 Saved Notes" key="draft">
                      <div style={{ padding: '16px 0' }}>
                        <DraftView
                          type="email"
                          data={emailDraftData}
                          loading={draftLoading}
                          onEditDraft={handleEditDraft}
                          onDeleteDraft={handleDeleteDraft}
                        />
                      </div>
                    </TabPane>
                  </Tabs>
                </div>
              </TabPane>
            </>
          )}
        </Tabs>
      </Modal>
    </>
  );
};

export default SendNoteModal;

